import type { ComponentMaintenanceData, CostSource, MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';
import type { Attachment } from '../../../../types/attachments';

/**
 * What the cost modal saves for one activity or replacement.
 *
 * Field lifecycle per source:
 * - `template` (schablon): no cost, unit price, comment or attachments.
 * - `customRate` (anpassad schablon): `customUnitPrice`; the cost is computed live.
 * - `custom` (kostnad enligt offert): the stored total.
 * Comment and attachments belong to both non-template sources and survive a
 * switch between them.
 */
export interface CostSettings {
    costSource: CostSource;
    /** The offert total; only with `custom` */
    customCost?: number;
    /** The own unit price; only with `customRate` */
    customUnitPrice?: number;
    comment: string;
    attachments: { [key: string]: Attachment };
    investmentPercentage: number;
}

type Attachments = { [key: string]: Attachment };

/** Present and non-empty, else undefined (the field is absent, not empty) */
const nonEmpty = (attachments: Attachments | undefined): Attachments | undefined =>
    attachments && Object.keys(attachments).length > 0 ? attachments : undefined;

const clampPercentage = (percentage: number): number => Math.max(0, Math.min(100, percentage || 0));

export const applyActivityCostSettings = (
    activity: MaintenanceActivity,
    settings: CostSettings,
    lastUpdated: string
): MaintenanceActivity => {
    const {
        cost: _cost,
        costSource: _costSource,
        customUnitPrice: _customUnitPrice,
        costComment: _costComment,
        costAttachments: _costAttachments,
        investmentPercentage: _investmentPercentage,
        ...rest
    } = activity;
    const updated: MaintenanceActivity = { ...rest, lastUpdated };

    if (settings.costSource === 'custom' && settings.customCost !== undefined) {
        updated.costSource = 'custom';
        updated.cost = settings.customCost;
    } else if (settings.costSource === 'customRate' && settings.customUnitPrice !== undefined) {
        updated.costSource = 'customRate';
        updated.customUnitPrice = settings.customUnitPrice;
    } else {
        updated.costSource = 'template';
    }

    if (updated.costSource !== 'template') {
        const comment = settings.comment.trim();
        if (comment) updated.costComment = comment;
        const attachments = nonEmpty(settings.attachments);
        if (attachments) updated.costAttachments = attachments;
    }

    const percentage = clampPercentage(settings.investmentPercentage);
    if (percentage) updated.investmentPercentage = percentage;

    return updated;
};

export const applyReplacementCostSettings = <T extends ComponentMaintenanceData>(
    entry: T,
    settings: CostSettings
): T => {
    const {
        replacementCost: _cost,
        replacementCostSource: _costSource,
        replacementCustomUnitPrice: _customUnitPrice,
        replacementCostComment: _costComment,
        replacementAttachments: _attachments,
        replacementInvestmentPercentage: _investmentPercentage,
        ...rest
    } = entry;
    const updated = { ...rest } as T;

    if (settings.costSource === 'custom' && settings.customCost !== undefined) {
        updated.replacementCostSource = 'custom';
        updated.replacementCost = settings.customCost;
    } else if (settings.costSource === 'customRate' && settings.customUnitPrice !== undefined) {
        updated.replacementCostSource = 'customRate';
        updated.replacementCustomUnitPrice = settings.customUnitPrice;
    } else {
        updated.replacementCostSource = 'template';
    }

    if (updated.replacementCostSource !== 'template') {
        const comment = settings.comment.trim();
        if (comment) updated.replacementCostComment = comment;
        const attachments = nonEmpty(settings.attachments);
        if (attachments) updated.replacementAttachments = attachments;
    }

    const percentage = clampPercentage(settings.investmentPercentage);
    if (percentage) updated.replacementInvestmentPercentage = percentage;

    return updated;
};

/** Attachments in `before` whose key is not in `after` */
export const droppedAttachments = (
    before: Attachments | undefined,
    after: Attachments | undefined
): Attachments => {
    const dropped: Attachments = {};
    Object.entries(before ?? {}).forEach(([key, attachment]) => {
        if (!after?.[key]) dropped[key] = attachment;
    });
    return dropped;
};

/**
 * Storage paths of every cost attachment a plan holds (activities and
 * replacements). Copying activities shares attachments between components, and
 * activation carries them into the active plan and its archives, so a removed
 * attachment may still be in use elsewhere.
 */
export const costAttachmentPaths = (plan: { components?: Record<string, ComponentMaintenanceData> } | null | undefined): Set<string> => {
    const paths = new Set<string>();
    const add = (attachments: Attachments | undefined): void => {
        Object.values(attachments ?? {}).forEach(attachment => {
            if (attachment?.storagePath) paths.add(attachment.storagePath);
        });
    };
    Object.values(plan?.components ?? {}).forEach(entry => {
        add(entry?.replacementAttachments);
        Object.values(entry?.maintenanceActivities ?? {}).forEach(activity => add(activity?.costAttachments));
    });
    return paths;
};

/** Whether a switch to `next` would drop a cost comment or attachments the row has */
export const switchDropsCostData = (
    next: CostSource,
    current: { comment?: string; attachments?: Attachments }
): boolean =>
    next === 'template' && (!!current.comment?.trim() || Object.keys(current.attachments ?? {}).length > 0);
